import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { feedbackService } from '../../../services/feedbackService';

const RATING_OPTIONS = [
  { value: 1, emoji: '😟', label: 'Poor' },
  { value: 2, emoji: '😐', label: 'Okay' },
  { value: 3, emoji: '🙂', label: 'Good' },
  { value: 4, emoji: '😊', label: 'Great' },
  { value: 5, emoji: '🤩', label: 'Amazing' },
];

const FeedbackSection = () => {
  const [rating, setRating] = useState(null);
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError(null);

    if (!message?.trim()) {
      setError('Please share your feedback before submitting.');
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await feedbackService.submitFeedback({
        email,
        rating,
        message,
        page: 'try-depth-me-beta',
      });

      if (result?.success) {
        setSubmitted(true);
        setRating(null);
        setMessage('');
        setEmail('');
      } else {
        setError(result?.error || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="feedback-section" className="py-12 px-6 relative">
      <div className="max-w-2xl mx-auto">
        <div className="group relative glassmorphism p-8 rounded-2xl">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 to-blue-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl" />
          <div className="relative z-10">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500/20 to-blue-500/20 flex items-center justify-center mx-auto mb-4">
              <Icon name="MessageCircle" size={24} className="text-primary" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3 text-center">
              Help us improve DepthMe
            </h2>
            <p className="text-muted-foreground mb-6 max-w-lg mx-auto text-center">
              We're building this with real user feedback. Tell us what works, what feels off, and what you want more of.
            </p>

            {submitted ? (
              <div className="text-center py-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-accent/20 flex items-center justify-center">
                  <Icon name="CheckCircle" size={32} className="text-accent" />
                </div>
                <h3 className="text-xl font-bold text-foreground mb-2">Thank you!</h3>
                <p className="text-muted-foreground mb-4">Your feedback helps us make DepthMe better for everyone.</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSubmitted(false)}
                >
                  Send More Feedback
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Rating */}
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    How's your experience so far?
                  </label>
                  <div className="flex items-center justify-center gap-3">
                    {RATING_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setRating(opt.value)}
                        className={`flex flex-col items-center gap-1 p-2 rounded-xl transition-all ${
                          rating === opt.value
                            ? 'bg-primary/20 border border-primary/50 scale-110'
                            : 'bg-white/5 border border-transparent hover:bg-white/10'
                        }`}
                      >
                        <span className="text-2xl">{opt.emoji}</span>
                        <span className="text-[10px] text-muted-foreground">{opt.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="feedback-message" className="block text-sm font-medium text-foreground mb-2">
                    Your feedback <span className="text-error">*</span>
                  </label>
                  <textarea
                    id="feedback-message"
                    rows={4}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="What works well? What feels off? What do you want more of?"
                    className="w-full rounded-xl bg-white/5 border border-border/50 text-foreground placeholder-muted-foreground px-4 py-3 text-sm focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30 resize-none"
                  />
                </div>

                {/* Email (optional) */}
                <div>
                  <label htmlFor="feedback-email" className="block text-sm font-medium text-foreground mb-2">
                    Email <span className="text-muted-foreground font-normal">(optional — if you'd like a response)</span>
                  </label>
                  <input
                    id="feedback-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your.email@example.com"
                    className="w-full rounded-xl bg-white/5 border border-border/50 text-foreground placeholder-muted-foreground px-4 py-3 text-sm focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30"
                  />
                </div>

                {error && (
                  <p className="text-error text-sm text-center">{error}</p>
                )}

                <div className="text-center pt-1">
                  <Button
                    type="submit"
                    variant="default"
                    size="lg"
                    loading={isSubmitting}
                    className="violet-glow px-8"
                    iconName="Send"
                    iconPosition="right"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Feedback'}
                  </Button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeedbackSection;
